import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Text } from '@/components/ui/text';
import { AlertTriangle } from 'lucide-react';
import { useState, type FC } from 'react';

interface ResetOnboardingConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceName: string;
  onConfirm: () => void;
}

export const ResetOnboardingConfirmModal: FC<
  ResetOnboardingConfirmModalProps
> = ({ open, onOpenChange, workspaceName, onConfirm }) => {
  const [confirmationInput, setConfirmationInput] = useState('');

  const expectedPhrase = `delete ${workspaceName}`;
  const isConfirmEnabled =
    confirmationInput.toLowerCase() === expectedPhrase.toLowerCase();

  const handleConfirm = () => {
    if (!isConfirmEnabled) return;
    onConfirm();
    setConfirmationInput('');
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setConfirmationInput('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-red-100">
              <AlertTriangle className="h-4 w-4 text-red-600" />
            </div>
            <DialogTitle>Reset onboarding</DialogTitle>
          </div>
          <DialogDescription asChild>
            <div className="space-y-3 pt-2">
              <Text variant="body-sm" color="primary">
                You are about to reset the onboarding for{' '}
                <strong>{workspaceName}</strong>. All onboarding data for this
                customer will be deleted.{' '}
                <strong>This action is irreversible.</strong>
              </Text>
              <Text variant="body-sm" color="primary">
                To confirm, type{' '}
                <strong className="select-none">delete {workspaceName}</strong>{' '}
                below:
              </Text>
            </div>
          </DialogDescription>
        </DialogHeader>

        <Input
          value={confirmationInput}
          onChange={e => setConfirmationInput(e.target.value)}
          placeholder={`delete ${workspaceName}`}
          onKeyDown={e => {
            if (e.key === 'Enter' && isConfirmEnabled) {
              handleConfirm();
            }
          }}
          autoFocus
        />

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isConfirmEnabled}
          >
            Reset onboarding
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
